import {
  Component,
  Inject,
  OnInit,
  Optional,
  Renderer2,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  DestroyRef
} from '@angular/core';
import {
  PlatformassetPipe,
  PlatformInformationService,
  PlatformurlPipe
} from '../../../../../../projects/platform-manager/src/public_api';
import { SsrCookieService as CookieService } from 'ngx-cookie-service-ssr';
import { ApiService } from '../../../../core/services/api.service';
import { BROWSER_WINDOW } from '../../../../core/injection-tokens/browser-window';
import { CommonModule, DOCUMENT, Location } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalizeRouterModule } from '@gilsdav/ngx-translate-router';
import { ALPY_FLY_IN_ANIMATION } from '../../../../core/animations/ALPY_FLY_IN.animation';
import { PartnerInterface } from '../interfaces/partner-interface';

@Component({
  selector: 'app-logo-with-partner-referer',
  templateUrl: './logo-with-partner-referer.component.html',
  styleUrls: ['./logo-with-partner-referer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ALPY_FLY_IN_ANIMATION],
  imports: [CommonModule, RouterModule, LocalizeRouterModule, PlatformurlPipe, PlatformassetPipe]
})
export class LogoWithPartnerRefererComponent implements OnInit {
  @Input() variationLogo = '';

  partnerLogo: PartnerInterface;
  shouldDisplayPartnerRefererLogo: boolean;
  isMicrosite = false;
  isBrowser = false;
  productName = '';
  platformcode = this.platformInformationService.getCurrentPlatformShortcode();
  currentLang = this.translateService.currentLang;
  logoWidth: number;
  logoHeight: number;

  constructor(
    private platformInformationService: PlatformInformationService,
    private cookieService: CookieService,
    private apiService: ApiService,
    @Optional() @Inject(BROWSER_WINDOW) private window,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private changeDetectionRef: ChangeDetectorRef,
    protected router: Router,
    protected translateService: TranslateService,
    private destroyRef: DestroyRef
  ) {
    this.shouldDisplayPartnerRefererLogo = false;
    this.setLogoDimensions();
  }

  private setLogoDimensions() {
    switch (this.platformcode) {
      case 's2':
        this.logoWidth = 175;
        this.logoHeight = 672;
        break;
      case 'sb':
        this.logoWidth = 74;
        this.logoHeight = 603;
        break;
      case 'sf':
        this.logoWidth = 290;
        this.logoHeight = 123;
        break;
      case 'pf':
        this.logoWidth = 271;
        this.logoHeight = 102;
        break;
      case 'sm':
        this.logoWidth = 322;
        this.logoHeight = 66;
        break;
      case 'mc':
        this.logoWidth = 216;
        this.logoHeight = 63;
        break;
      case 'df':
        this.logoWidth = 281;
        this.logoHeight = 88;
        break;
      case 'du':
        this.logoWidth = 281;
        this.logoHeight = 88;
        break;
      case 'bp':
        this.logoWidth = 216;
        this.logoHeight = 63;
        break;
      default: // (ar)
        this.logoWidth = 277;
        this.logoHeight = 32;
        break;
    }
  }

  ngOnInit() {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: LangChangeEvent) => {
        this.currentLang = event.lang;
      });
    if (this.router.url && this.router.url.search('/partner-') > 0) {
      this.isMicrosite = true;
    }
    // We can only check referrer on client to prevent "wrong" cache responses with referrers.
    if (this.platformInformationService.getIsBrowser()) {
      this.updatePartnerAdds();
      this.isBrowser = true;
    }
  }

  updatePartnerAdds() {
    this.checkAndSetRefererCookie();
    // we want the cookies on partners now as well but not the styles
    if (!this.isMicrosite) {
      this.updatePartnerLogo();
    }
  }

  private getQueryParameterFromString(string, parameter) {
    let searchedParameter = null;
    const vars = {};
    if (string.indexOf(parameter) > -1) {
      string.replace(/[?&]+([^=&]+)=([^&]*)/gi, function (m, key, value) {
        vars[key] = value;
      });
      searchedParameter = vars[parameter];
    }
    return searchedParameter;
  }

  private checkAndSetRefererCookie() {
    const referer = this.window.document.referrer;
    const regX = new RegExp('^(?:f|ht)tp(?:s)?://([^/]+)', 'im');
    const refererFromQuery = this.getQueryParameterFromString(
      this.window.location.search.substring(0),
      'referer'
    );
    const payloadFromQuery = this.getQueryParameterFromString(
      this.window.location.search.substring(0),
      'refererPayload'
    );
    let hostArray = referer.match(regX);

    if (refererFromQuery) {
      hostArray = ['Referer', refererFromQuery];
    }

    if (hostArray) {
      const host = hostArray[1].toString();
      const ownHost = this.window.location.host;

      if (host !== ownHost) {
        this.setRefererCookie(host, payloadFromQuery);
      }
    }
  }

  private setRefererCookie(referer: string, payload?: string) {
    const date = new Date(Date.now() + 2592000000); // today + 30 days
    this.cookieService.set('Referer', JSON.stringify({ host: referer, payload: payload ? payload : '' }), {
      path: '/',
      expires: date
    });
  }

  private updatePartnerLogo() {
    let refererCookieValue = this.cookieService.get('Referer');
    if (refererCookieValue) {
      try {
        const parsedCookie = JSON.parse(refererCookieValue);
        if (parsedCookie['host']) {
          refererCookieValue = parsedCookie['host'];
        }
      } catch {
        // it was a string, the partner-logo-api can
        // fetch the host here as well - the code above
        // is only to prevent showing partner-logos,
        // if maybe one day a partner's name is "refererPayload"
      }
      this._updatePartnerLogo(refererCookieValue);
    }
  }

  private _updatePartnerLogo(referer: string) {
    this.apiService
      .getPartnerRefererInfo(referer)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: partnerLogo => {
          this.partnerLogo = partnerLogo;
          this.shouldDisplayPartnerRefererLogo = true;
          if (partnerLogo.custom_css) {
            this.addPartnerCss(partnerLogo.custom_css);
          }
          this.changeDetectionRef.markForCheck();
        },
        error: resp => {
          // backend returns 404 even if we have a partner but no logo for it. We should only remove the cookie
          // when the 404 response is for a non-existing partner. ARO-2589
          if (resp?.error !== 'No brand-logo') {
            this.cookieService.delete('Referer', '/');
          }
          this.shouldDisplayPartnerRefererLogo = false;
          this.changeDetectionRef.markForCheck();
        }
      });
  }

  private addPartnerCss(link: string) {
    const partnerCss = this.document.createElement('link');
    partnerCss.rel = 'stylesheet';
    partnerCss.id = 'additional-partner-css';
    partnerCss.href = Location.joinWithSlash(this.platformInformationService.getHostname(), link);
    this.renderer.appendChild(this.document.head, partnerCss);
  }
}
